<template>
  <div
    class="min-h-screen w-full bg-gradient-to-b from-stone-100 to-stone-300 dark:from-stone-950 dark:to-stone-800 font-share-tech"
  >
    <!-- Navbar -->
    <header
      class="fixed top-0 left-0 right-0 z-50 h-16 bg-white dark:bg-stone-950 border-b-2 border-stone-600 dark:border-amber-500"
    >
      <div class="flex items-center gap-2 h-full px-4">
        <!-- Mobile menu button -->
        <button
          v-if="$route.path !== '/Home'"
          @click="sidebarOpen = !sidebarOpen"
          type="button"
          class="inline-flex items-center p-2 text-sm text-stone-500 sm:hidden hover:bg-stone-300 dark:hover:bg-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-200 dark:focus:ring-stone-800 shrink-0"
          aria-controls="default-sidebar"
        >
          <span class="sr-only">{{ t("common.openSidebar") }}</span>
          <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path
              clip-rule="evenodd"
              fill-rule="evenodd"
              d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
            ></path>
          </svg>
        </button>

        <!-- Logo & Title -->
        <div class="flex items-center shrink-0">
          <a
            href="https://github.com/xfdxfdxfd"
            target="_blank"
            class="flex items-center"
          >
            <img
              v-if="theme === 'light'"
              class="h-8"
              src="../src/assets/github-mark.webp"
              alt="GitHub"
            />
            <img
              v-else
              class="h-8"
              src="../src/assets/github-mark-white.webp"
              alt="GitHub"
            />
            <span
              class="ml-4 text-2xl translate-y-0.5 font-semibold uppercase text-stone-950 dark:text-white"
              >xfdxfdxfd</span
            >
          </a>
        </div>

        <!-- Search + locale + theme (right-aligned) -->
        <div class="ml-auto flex items-center gap-2 shrink-0">
          <GlobalSearch class="hidden sm:block" />

          <button
            @click="toggleLocale"
            class="h-9 min-w-[2.25rem] px-2 shrink-0 text-sm font-bold rounded-lg border border-stone-300 dark:border-amber-500/40 bg-white/80 dark:bg-stone-900/80 text-stone-700 dark:text-amber-200 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
            :title="locale === 'en' ? t('common.switchToZh') : t('common.switchToEn')"
          >
            {{ locale === "en" ? t("common.switchToZh") : t("common.switchToEn") }}
          </button>

          <button @click="toggleTheme" class="p-2 shrink-0">
          <img
            v-if="theme === 'light'"
            class="h-8"
            src="../src/assets/sun.webp"
            :alt="t('common.lightMode')"
          />
          <img
            v-else
            class="h-8"
            src="../src/assets/moon.webp"
            :alt="t('common.darkMode')"
          />
          </button>
        </div>
      </div>
    </header>

    <!-- Sidebar -->
    <aside
      v-if="$route.path !== '/Home'"
      id="default-sidebar"
      :class="[
        'sidebar-3d fixed top-16 left-0 z-40 h-full transition-all duration-300 overflow-y-auto bg-transparent',
        sidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full w-64',
        'sm:translate-x-0 sm:w-64',
      ]"
      aria-label="Sidebar"
    >
      <div class="h-full px-2 py-4">
        <ul class="space-y-3 font-medium">
          <li
            v-for="(name, index) in navItems"
            :key="name"
            @click="
              currentTab = name;
              sidebarOpen = false;
            "
            role="button"
            class="nav-item-3d"
            :style="{ '--item-index': index }"
          >
            <RouterLink :to="'/' + name">
              <a
                :class="`flex items-center text-xl p-2 ${
                  $route.path === '/' + name
                    ? 'text-stone-900 dark:text-white bg-amber-300/90 dark:bg-amber-500/70 hover:bg-amber-500/90 dark:hover:bg-amber-700/70 border-amber-300/90 dark:border-amber-800/70'
                    : 'text-stone-900 dark:text-white bg-white/90 dark:bg-stone-950/70 hover:bg-stone-100/90 dark:hover:bg-stone-800/70 border-stone-300/90 dark:border-amber-500/70'
                } shadow-lg border   transition-all duration-200 backdrop-blur-sm w-16 h-16 hover:w-64`"
              >
                <!-- {{ $route.path === "/" + name }} -->
                <span class="nav-icon flex-shrink-0">
                  <Icon
                    v-if="name === 'Home'"
                    icon="mdi-light:home"
                    class="w-12 h-12"
                  />
                  <Icon
                    v-else-if="name === 'Profile'"
                    icon="mdi-light:account"
                    class="w-12 h-12"
                  />
                  <Icon
                    v-else-if="name === 'Project'"
                    icon="mdi-light:folder-multiple"
                    class="w-12 h-12"
                  />
                  <Icon
                    v-else-if="name === 'Resume'"
                    icon="mdi-light:file"
                    class="w-12 h-12"
                  />
                  <Icon
                    v-else-if="name === 'Blog'"
                    icon="mdi-light:comment-text"
                    class="w-12 h-12"
                  />
                </span>
                <span
                  class="nav-text ms-3 whitespace-nowrap overflow-hidden transition-all duration-300"
                  >{{ t(`nav.${name}`) }}</span
                >
              </a>
            </RouterLink>
          </li>
        </ul>
      </div>
    </aside>

    <!-- Overlay for mobile -->
    <div
      v-if="sidebarOpen"
      @click="sidebarOpen = false"
      class="fixed top-16 left-0 right-0 bottom-0 z-30 bg-black/70 backdrop-blur-sm sm:hidden"
    ></div>

    <!-- Main content -->
    <main class="pt-28 sm:pt-16 transition-all duration-300">
      <router-view v-slot="{ Component }">
        <transition name="page" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>

    <!-- Mobile search -->
    <div class="fixed top-16 left-0 right-0 z-40 px-4 py-2 sm:hidden bg-white/90 dark:bg-stone-950/90 backdrop-blur-sm border-b border-stone-300 dark:border-amber-500/30">
      <GlobalSearch />
    </div>

    <!-- AI chat widget -->
    <ChatWidget />
  </div>
</template>

<script setup lang="ts">
import { ref, provide } from "vue";
import { useI18n } from "vue-i18n";
import { Icon } from "@iconify/vue";
import GlobalSearch from "@/components/GlobalSearch.vue";
import ChatWidget from "@/components/ChatWidget.vue";
import type { AppLocale } from "@/i18n";

type Theme = "light" | "dark";

const { locale, t } = useI18n();

const theme = ref<Theme>("light");
const currentTab = ref("Intro");
const sidebarOpen = ref(false);
const navItems = ["Home", "Profile", "Project", "Resume", "Blog"];

provide("theme", () => theme.value);

const toggleLocale = (): void => {
  const next: AppLocale = locale.value === "en" ? "zh-TW" : "en";
  locale.value = next;
  localStorage.setItem("locale", next);
};

const toggleTheme = (): void => {
  if (document.documentElement.classList.contains("dark")) {
    document.documentElement.classList.remove("dark");
    localStorage.theme = "light";
    theme.value = "light";
  } else {
    document.documentElement.classList.add("dark");
    localStorage.theme = "dark";
    theme.value = "dark";
  }
};

// Theme initialization
if (localStorage.theme === "light") {
  document.documentElement.classList.remove("dark");
  theme.value = "light";
} else {
  document.documentElement.classList.add("dark");
  theme.value = "dark";
  localStorage.theme = "dark";
}
</script>

<style scoped>
.page-enter-active {
  animation: page-in 0.4s ease-out;
}
.page-leave-active {
  animation: page-out 0.3s ease-in;
}

@keyframes page-in {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes page-out {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

.sidebar-3d {
  transform-style: preserve-3d;
  perspective: 1000px; /* can be on parent instead */
}

/* .sidebar-3d:hover .nav-item-3d {
  transform: rotateY(-45deg);
} */

/* Collapsed state - hide text */
@media (min-width: 640px) {
  .nav-item-3d:hover {
    transform-style: preserve-3d;
    transform: perspective(1000px) rotateY(25deg);
    transform-origin: left center;
    transition: transform 0.6s ease;
  }
  .nav-item-3d:not(:hover) .nav-text {
    width: 0;
    opacity: 0;
  }

  .nav-item-3d:hover .nav-text {
    width: auto;
    opacity: 1;
  }
}
</style>
